import {
  useGetBidComparisonQuery,
  useGetBlobReferenceCountQuery,
  usePackageId,
  useProjectId,
} from '@consigli/hooks';
import { useMemo } from 'react';

import { BID_LEVELLING_REFERENCE } from './offer-references';

import type { BidComparisonData, BidComparisonRow } from './bid-comparison-types';

const EMPTY_DATA: BidComparisonData = {
  summary: '',
  bidsCompared: 0,
  itemsLeveled: 0,
  totalWeighting: 0,
  contractors: [],
  rows: [],
};

/**
 * The ranking's "not comparable" bucket: a requirement a difference between bids
 * would not change the decision on. Real priorities run 1 (highest) to 10 (lowest);
 * a row in this bucket is hidden rather than shown at the bottom.
 */
export const NOT_COMPARABLE_RANK = 11;

/** Drop the rows the ranking judged not worth comparing, so the table shows only
 * priority buckets 1-10. */
export const hideNotComparableRows = (rows: readonly BidComparisonRow[]): BidComparisonRow[] =>
  rows.filter((row) => row.rank < NOT_COMPARABLE_RANK);

/**
 * Order rows by AI rank, lowest number (highest priority) first. Stable, so
 * rows sharing a rank keep the order the server sent them in. Ranks run 1
 * (highest priority) to 10 (lowest), so unranked rows fall to the bottom without
 * any special-casing here.
 */
export const sortRowsByRank = (rows: readonly BidComparisonRow[]): BidComparisonRow[] =>
  [...rows].sort((a, b) => a.rank - b.rank);

interface BidComparisonResult {
  data: BidComparisonData;
  isLoading: boolean;
}

/**
 * Fetch the Bid Comparison (bid leveling) table for the current Offer package.
 *
 * The grouping-by-requirement and per-contractor cell shaping happens server
 * side (`findings/bid-comparison/`), so the client receives a small aggregated
 * payload instead of every GAPS finding — which previously timed out (504).
 *
 * A bid-levelling worksheet, once uploaded, gives the table its own row order.
 * Without one, the rows are ordered by the AI rank so the highest-priority
 * requirements sit at the top, and the ones the ranking judged not worth
 * comparing (rank 11) are hidden.
 */
export const useBidComparisonData = (): BidComparisonResult => {
  const projectId = useProjectId();
  const packageId = usePackageId();

  const { data, isLoading } = useGetBidComparisonQuery({ projectId, packageId });
  const { data: referenceCounts = {} } = useGetBlobReferenceCountQuery({ projectId, packageId });

  const hasLevellingSheet = (referenceCounts[BID_LEVELLING_REFERENCE] ?? 0) > 0;

  const shaped = useMemo<BidComparisonData>(() => {
    const source: BidComparisonData = data ?? EMPTY_DATA;
    if (hasLevellingSheet) {
      return source;
    }
    return { ...source, rows: sortRowsByRank(hideNotComparableRows(source.rows)) };
  }, [data, hasLevellingSheet]);

  return { data: shaped, isLoading };
};
